import React from 'react';
import { makeStyles, Button, Switch, Text, Spinner, tokens } from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { RefreshControlProps } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  refreshButton: {
    minWidth: '120px',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  lastUpdated: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

export const RefreshControl: React.FC<RefreshControlProps> = ({
  autoRefresh,
  onToggleAutoRefresh,
  lastUpdated,
  onManualRefresh,
  isRefreshing,
}) => {
  const styles = useStyles();

  const formatLastUpdated = (timestamp: string | null): string => {
    if (!timestamp) {
      return 'Never';
    }

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) {
      return `${diffSecs}s ago`;
    }

    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }

    return date.toLocaleTimeString();
  };

  return (
    <div className={styles.container}>
      <Button
        appearance="subtle"
        icon={isRefreshing ? undefined : <ArrowSyncRegular />}
        onClick={onManualRefresh}
        disabled={isRefreshing}
        className={styles.refreshButton}
      >
        {isRefreshing ? (
          <div className={styles.spinner}>
            <Spinner size="tiny" />
            <span>Refreshing...</span>
          </div>
        ) : (
          'Refresh Now'
        )}
      </Button>

      <div className={styles.toggleContainer}>
        <Switch checked={autoRefresh} onChange={onToggleAutoRefresh} label="Auto-refresh (60s)" />
      </div>

      {lastUpdated && (
        <Text className={styles.lastUpdated}>Updated {formatLastUpdated(lastUpdated)}</Text>
      )}
    </div>
  );
};
